import FormMandatoryMention from 'app/components/Form/MandatoryMention';
import React, { useCallback, useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormSetValue,
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Icon, Label } from 'semantic-ui-react';
import { Patient } from 'app/interfaces/Patient';
import TeleradiologyVetFlowModalAnalyzeRequestTermsOfSales from './TermsOfSales';
import _ from 'lodash';
import PatientInfoForm, {
  FieldLayout,
  FieldRules,
  OnChangePayload,
} from 'app/components/PatientInfoForm';

export type FormDataType = {
  isUrgent?: boolean;
  examDate: string | Date;
  reason: string;
  clinicalSigns: string;
  veterinarianInterpretation: string;
  examiningVeterinarian?: string;
  examiningEmail?: string;
  referentClinic?: string;
  referentVeterinarian?: string;
  referentEmail?: string;
  patientName: string;
  patientSpecie: string;
  patientSpecieCustom?: string;
  patientRace: string;
  patientSex: string;
  patientBirthDate: string | Date;
  patientWeight?: string;
  ownerLastName: string;
  acceptGeneralTermsOfSales: boolean;

  // Unused fields below but kept for safekeeping
  patientAge?: string;
  ownerFirstName?: string;
  ownerAddress?: string;
  ownerCity?: string;
  ownerPostCode?: string;
  ownerCountryCode?: string;
  ownerPhoneNumber?: string;
  ownerEmail?: string;
};

type TeleradiologyVetFlowModalAnalyzeRequestFormProps = {
  animal?: Patient;
  onAnimalChange?: (animal: Patient) => void;
  scrollToTop: () => void;
  onSubmit: (formData: FormDataType) => void;
  control: Control<any>;
  errors: FieldErrors;
  handleSubmit: UseFormHandleSubmit<FormDataType>;
  setValue: UseFormSetValue<any>;
};

function TeleradiologyVetFlowModalAnalyzeRequestForm({
  animal,
  onAnimalChange,
  scrollToTop,
  onSubmit,
  control,
  errors,
  handleSubmit,
  setValue,
}: TeleradiologyVetFlowModalAnalyzeRequestFormProps) {
  // const setFakeOwnerDetails = () => {
  //   ApiCalls.getUserDetails().then((response) => {
  //     setValue('ownerAddress', response.data.user.address.address);
  //     setValue('ownerPostCode', response.data.user.address.postCode);
  //     setValue('ownerCity', response.data.user.address.city);
  //     setValue('ownerPhoneNumber', response.data.user.address.phoneNumber);
  //   });
  // };

  // useEffect(() => {
  //   setFakeOwnerDetails();
  // }, []);

  return (
    <>
      <FormMandatoryMention />

      <Form onSubmit={handleSubmit(onSubmit, scrollToTop)}>
        <FieldsetAnalyseExam control={control} errors={errors} />

        <FieldsetAnalysePatient animal={animal} onAnimalChange={onAnimalChange} errors={errors} />

        <Controller
          name="acceptGeneralTermsOfSales"
          control={control}
          rules={{
            required: 'teleradiology.analyze-request.accept-general-terms-of-sales.error.required',
          }}
          render={({ field: { onChange, onBlur, value } }) => (
            <Form.Checkbox
              required
              label={
                <label
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(!value);
                  }}
                >
                  <FormattedMessage id="teleradiology.analyze-request.accept-general-terms-of-sales.label" />
                  &nbsp;
                  <TeleradiologyVetFlowModalAnalyzeRequestTermsOfSales
                    onAcceptClick={() => onChange(true)}
                  />
                </label>
              }
              checked={value}
              onChange={(_event, { checked }) => onChange(checked)}
              onBlur={onBlur}
              error={!!errors.acceptGeneralTermsOfSales}
            />
          )}
        />
      </Form>
    </>
  );
}

function FieldsetAnalyseExam({ control, errors }: { control: Control<any>; errors: FieldErrors }) {
  const intl = useIntl();
  return (
    <fieldset id="analyze-exam">
      <legend>
        <FormattedMessage id="teleradiology.analyze-request.exam.label" />
      </legend>
      <Form.Group widths="equal">
        <Controller
          name="isUrgent"
          control={control}
          render={({ field: { onChange, onBlur, value, name } }) => (
            <Form.Checkbox
              label={intl.formatMessage({
                id: 'teleradiology.analyze-request.is-urgent.label',
              })}
              checked={value}
              onChange={(_event, { checked }) => onChange(checked)}
              onBlur={onBlur}
              error={!!errors.isUrgent}
            />
          )}
        />
        <Controller
          name="examDate"
          control={control}
          rules={{
            required: 'teleradiology.analyze-request.exam-date.error.required',
          }}
          render={({ field: { onChange, onBlur, value } }) => (
            <Form.Input
              type="date"
              required
              readOnly
              labelPosition="left"
              label={intl.formatMessage({
                id: 'teleradiology.analyze-request.exam-date.label',
              })}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.examDate}
            >
              <Label>
                <Icon fitted name="calendar alternate outline" />
              </Label>
              <input />
            </Form.Input>
          )}
        />
      </Form.Group>

      <Controller
        name="reason"
        control={control}
        rules={{
          required: 'teleradiology.analyze-request.reason.error.required',
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <Form.TextArea
            required
            label={intl.formatMessage({ id: 'teleradiology.analyze-request.reason.label' })}
            placeholder={intl.formatMessage({
              id: 'teleradiology.analyze-request.reason.placeholder',
            })}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            error={!!errors.reason}
          />
        )}
      />
      <Controller
        name="clinicalSigns"
        control={control}
        rules={{
          required: 'teleradiology.analyze-request.clinical-signs.error.required',
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <Form.TextArea
            required
            label={intl.formatMessage({
              id: 'teleradiology.analyze-request.clinical-signs.label',
            })}
            placeholder={intl.formatMessage({
              id: 'teleradiology.analyze-request.clinical-signs.placeholder',
            })}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            error={!!errors.clinicalSigns}
          />
        )}
      />
      <Controller
        name="veterinarianInterpretation"
        control={control}
        rules={{
          required: 'teleradiology.analyze-request.veterinarian-interpretation.error.required',
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <Form.TextArea
            required
            label={intl.formatMessage({
              id: 'teleradiology.analyze-request.veterinarian-interpretation.label',
            })}
            placeholder={intl.formatMessage({
              id: 'teleradiology.analyze-request.veterinarian-interpretation.placeholder',
            })}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            rows={6}
            error={!!errors.veterinarianInterpretation}
          />
        )}
      />
      <Form.Group widths="equal">
        <Controller
          name="examiningVeterinarian"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Form.Input
              type="text"
              labelPosition="left"
              fluid
              label={intl.formatMessage({
                id: 'teleradiology.analyze-request.examining-veterinarian.label',
              })}
              placeholder={intl.formatMessage({
                id: 'teleradiology.analyze-request.examining-veterinarian.placeholder',
              })}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.examiningVeterinarian}
            >
              <Label>
                <Icon fitted name="doctor" />
              </Label>
              <input />
            </Form.Input>
          )}
        />
        <Controller
          name="examiningEmail"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Form.Input
              type="email"
              labelPosition="left"
              fluid
              label={intl.formatMessage({
                id: 'teleradiology.analyze-request.examining-email.label',
              })}
              placeholder={intl.formatMessage({
                id: 'teleradiology.analyze-request.examining-email.placeholder',
              })}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.examiningEmail}
            >
              <Label>
                <Icon fitted name="at" />
              </Label>

              <input />
            </Form.Input>
          )}
        />
      </Form.Group>
      <Controller
        name="referentClinic"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <Form.Input
            type="text"
            labelPosition="left"
            fluid
            label={intl.formatMessage({
              id: 'teleradiology.analyze-request.referent-clinic.label',
            })}
            placeholder={intl.formatMessage({
              id: 'teleradiology.analyze-request.referent-clinic.placeholder',
            })}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            error={!!errors.referentClinic}
          >
            <Label>
              <Icon fitted name="home" />
            </Label>

            <input />
          </Form.Input>
        )}
      />
      <Form.Group widths="equal">
        <Controller
          name="referentVeterinarian"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Form.Input
              type="text"
              labelPosition="left"
              fluid
              label={intl.formatMessage({
                id: 'teleradiology.analyze-request.referent-veterinarian.label',
              })}
              placeholder={intl.formatMessage({
                id: 'teleradiology.analyze-request.referent-veterinarian.placeholder',
              })}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.referentVeterinarian}
            >
              <Label>
                <Icon fitted name="doctor" />
              </Label>
              <input />
            </Form.Input>
          )}
        />
        <Controller
          name="referentEmail"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Form.Input
              type="email"
              labelPosition="left"
              fluid
              label={intl.formatMessage({
                id: 'teleradiology.analyze-request.referent-email.label',
              })}
              placeholder={intl.formatMessage({
                id: 'teleradiology.analyze-request.referent-email.placeholder',
              })}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.referentEmail}
            >
              <Label>
                <Icon fitted name="at" />
              </Label>
              <input />
            </Form.Input>
          )}
        />
      </Form.Group>
    </fieldset>
  );
}

// Field rules for PatientInfoForm - which fields are required
const PATIENT_FIELD_RULES: FieldRules = {
  name: { required: true },
  specie: { required: true },
  race: { required: true },
  sex: { required: true },
  birth_date: { required: true },
  owner_name: { required: true },
};

// Layout for PatientInfoForm - how fields should be arranged
const PATIENT_FIELD_LAYOUT: FieldLayout = [
  ['file_id', 'owner_name', 'name'],
  ['chip_id', 'specie', 'birth_date'],
  ['pedigree_id', 'race', ['sex', 'weight']],
];

// Helper to format date for form
const formatDateForForm = (date?: Date): string => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function FieldsetAnalysePatient({
  animal,
  onAnimalChange,
  errors,
}: {
  animal?: Patient;
  onAnimalChange?: (animal: Patient) => void;
  errors?: FieldErrors<FormDataType>;
}) {
  const handlePatientInfoChange = useCallback(
    ({ patientInfo, hasConflict }: OnChangePayload) => {
      // We only let patientInfo without conflitcs go through, otherwise we show the conflict to the user and let them decide what to do
      if (hasConflict) return;
      onAnimalChange(patientInfo);
    },
    [onAnimalChange]
  );

  const errorsForPatientInfoForm = useMemo(() => {
    if (!errors) return {};

    const formattedErrors = {
      name: errors.patientName?.message,
      specie: errors.patientSpecie?.message,
      race: errors.patientRace?.message,
      sex: errors.patientSex?.message,
      birth_date: errors.patientBirthDate?.message,
      owner_name: errors.ownerLastName?.message,
    };
    return formattedErrors;
  }, [
    errors.patientName,
    errors.patientSpecie,
    errors.patientRace,
    errors.patientSex,
    errors.patientBirthDate,
    errors.ownerLastName,
  ]);

  return (
    <fieldset id="analyze-patient">
      <legend>
        <FormattedMessage id="teleradiology.analyze-request.patient.label" />
      </legend>
      <PatientInfoForm
        onChange={handlePatientInfoChange}
        initialPatientData={animal}
        fieldRules={PATIENT_FIELD_RULES}
        fieldLayout={PATIENT_FIELD_LAYOUT}
        className="patient-info-form_acquisition"
        fieldErrors={errorsForPatientInfoForm}
        menuPlacement="top"
      />
    </fieldset>
  );
}

export default TeleradiologyVetFlowModalAnalyzeRequestForm;
